import React from "react";

import { images } from "../../constants";
import "./Papermaking.css";
import { SubHeading, YoutubeEmbed } from "../../components";

const cardboard_styles = {
  transform: "rotate(-90deg)",
  margin: "auto",
};

const recycled_paper_styles = {
  margin: "auto",
};
const Papermaking = () => (
  <div className="app__bg app__wrapper section__padding">
    <div className="app__wrapper_info">
      <h1 className="headtext__cormorant">Papermaking</h1>

      <div className="app__author-content">
        <SubHeading title="Making a Mould and Deckle"></SubHeading>
        <div className="app__author-content_quote">
          <p className="p__opensans_lower_case flex__center">
            I took two picture frames and an article of mesh fabric clothing
            from a local thrift store in order to make my own mould and deckle.
            A mould and deckle is an instrument used in hand papermaking. The
            use of moulds and deckles in paper mills date back to as far as the
            15th century. However, other forms of papermaking/book readership
            have predated the mould and deckle instrument, such as the use of
            papyrus paper by the ancient Egyptians, and the use of bamboo/wooden
            slips by the ancient Chinese.
          </p>
        </div>
        <SubHeading title="Making Transfer Sheets" />
        <p className="p__opensans_lower_case flex__center">
          I recycled 3 articles of clothing made from 100% cotton fabric. In
          total I was able to create 35 transfer sheets from the material, which
          would mean that in every papermaking session I would be able to make
          35 sheets of recycled paper before hanging them up to dry for one-two
          days.
        </p>
        <SubHeading title="Recycled Paper" />
        <p className="p__opensans_lower_case flex__center">
          While working at the Cinematic Arts Library, we shredded a significant
          amount of scripts set in typewriter font on good quality paper.
          According to several Californian curbside recycling guidelines (such
          as that of Los Angeles, Torrance, and Sacramento), shredded paper is
          suggested to be thrown out in the black bins, or if they are being
          recycled in the blue bins–to follow specific guidelines such as
          packing the shredded paper in a paper or recyclable plastic bag. Most
          all government recycling agencies seem to agree that more than
          anything, individuals should shred selectively, and opt to compost the
          shredded paper rather than putting them in the recycling bin.
          According to Amy Y. Conry Davis, the reason for this is because “the
          process of shredding paper breaks down the strength of its natural
          fibers, which impacts its value and recyclability…once paper products
          have been cut into shreds they can’t undergo the same sorting process
          as intact pieces like newspapers and cardboard boxes.”Shredded paper
          can be recycled, just like normal paper. However, the shredded paper
          presents a number of challenges to recycling centers (Andrew
          Krosofsky).
        </p>
        <YoutubeEmbed embedId="PhSxyUzSxAw" />
        <p className="p__opensans_lower_case flex__center">
          That is why for my project I took a 15 gallon bag full of shredded
          paper from the library recycle bin, to serve as the crux of my
          recycled papermaking. In total I made over 150 new pieces of paper by
          using this material alone.
        </p>
        <SubHeading title="Mould and Deckle" />
        <YoutubeEmbed embedId="SkDzjrBJ1EY" />
        <p className="p__opensans_lower_case flex__center">
          The technique used in this video is not unlike a traditional paper
          production system, whereby the natural materials (such as wood fibers,
          in this case recycled shredded scripts) are made into a pulp, which is
          then poured into water as a mode of suspension, lubrication, and
          movement to create new paper using the mould and deckle instrument as
          a template and suspension device. The process seen in this video, and
          used by myself for all further processes in paper making, is further
          simplified for at home use (See NevermindPaper).
        </p>
        <SubHeading title="Ancillary Steps" />
        <p className="p__opensans_lower_case flex__center">
          -Hang the paper to dry (using a clothing rack and clothespins), ranges
          from 1-2 days -Once the paper has completely dried, remove from hanger
          and peel off the paper from the transfer sheets -Flatten out the paper
          using a bookmaking tool, or by simply using books and paperweights
        </p>
        <br />
        <a
          href="https://bookbindingcyp.weebly.com/"
          target="_blank"
          title="Bookbinding"
        >
          Bookbinding page
        </a>
        <h2 className="large_subheader">Experimentation</h2>
        <SubHeading title="A4 paper" />
        <p className="p__opensans_lower_case flex__center">
          The materials used for this sample were a mix of recycled A4 paper
          from class projects and syllabuses.
        </p>
        <SubHeading title="Cardstock paper" />
        <p className="p__opensans_lower_case flex__center">
          The materials used for this sample were a mix of recycled A4 paper
          from class projects and syllabuses, and thin cardstock products such
          as food packaging and business cards.
        </p>
        <SubHeading title="Cardboard and Mixed Media" />
        <p className="p__opensans_lower_case flex__center">
          The materials used for this sample were a mix of recycled cardboard
          boxes and a collection of recycled magazines and articles. While
          breaking down the material into a pulp, there are moments where a thin
          layer of plastic came off from the lamination process which gives
          certain magazine covers and images a sheen/glossy finish. The overall
          consistency of this material pulp was very thick.
        </p>
        <SubHeading title="Results" />
        <div className="app__aboutus-content_center">
          <img
            style={cardboard_styles}
            src={images.Cardstock}
            alt="cardstock paper comparisons"
          />
          <p className="p__opensans_lower_case flex__center">
            From left to right, the A4 paper, the cardstock paper, and the
            cardboard and mixed media paper. With regards to color, smell and
            consistency, they all had similar results, with the exception of the
            cardboard paper which did have a distinctly unpleasant odor.
            Cardboard naturally has an odorous quality once wetted. Whether it
            was the cardboard, or the cardboard in conjunction with being
            combined with other mixed media print is unsure. The odor of the
            cardboard paper made it very unpleasurable and an unlikely viable
            substitute for standard recycled papermaking.{" "}
          </p>

          <br />
          <img
            style={recycled_paper_styles}
            src={images.RecycledPaper}
            alt="Pictures of the recycled paper"
          />
          <br />

          <p className="p__opensans_lower_case flex__center">
            The recycled paper was also able to be printed on. However, the
            paper thickness played a big role in the viability of the recycled
            paper going through the printer. See on the far right the recycled
            paper with words and lines. That is the A4 Paper which was
            successfully printed on after going through a traditional printer.
            The paper on the far left (ripped up) was the cardstock paper, which
            unfortunately caused a paper jam. The paper underneath the A4 sample
            is the cardboard paper, which was unable to go through the printer
            for the same reason. The thickness of the paper, however (with the
            exception of cardboard) was more of an issue with the craftsmanship
            and technique of the papermaking process, rather than an issue with
            the materials. A finely shredded cardstock paper which is
            transferred onto the mould as a thin strip, as opposed to a more
            clumpy pulp mixture of recycled A4 paper, would result in an inverse
            result to what was observed above. Craftsmanship and technique
            played a big role in the variations of paper thickness, and was a
            constantly changing variable due to human fallibility. All paper
            types, however, were able to be written upon with pencil and pen,
            and even collect water color paint as well, with the same degree of
            performance and results across the board (the natural thickness and
            cotton transfer sheet markings of the recycled paper almost made
            them a make-shift water color paper).
          </p>
        </div>
        <SubHeading title="Discussion" />
        <p className="p__opensans_lower_case flex__center">
          According to the American Forest & Paper Association, in 2021 more
          than 50 million tons of paper was recovered for recycling. The
          recycling rate for paper was 68% while the recycling rate for
          cardboard (corrugated) was 91.4%. 80% of U.S. paper mills use some
          recycled paper to make new and innovative products, such as boxes for
          food and medicine, tissue products, and newspapers. The paper
          recycling process works like this: local recycling haulers bring
          recycled items to a materials recovery facility (MRF) where recycled
          materials are sorted into like things. Workers remove contaminants,
          separate the items into cardboard, mixed paper, office paper, etc.,
          and lastly bale and ship materials to a paper mill to be turned into
          new products (American Forest & Paper Association). The American
          Forest & Paper Association suggests for the viability of more paper
          products to be recycled, individuals keep their recyclable paper
          products dry and clean.
        </p>
      </div>
    </div>
  </div>
);

export default Papermaking;
